import type { IPeerConnection } from './types';
import type { WebRtcOptimizationConfig, WebRtcOptimizationManager } from './WebRtcOptimizationManager';
import { REQUEST_KEYFRAME_MESSAGE } from './WebRtcOptimizationManager';
import { IceHealthGuard } from './IceHealthGuard';
import type { GuestPeerSessionCallbacks, GuestPeerLogger } from './guestPeerSessionTypes';
import { createGuestPlayerActivityMonitor } from './guestPlayerActivity';

const KEYFRAME_RETRY_MS = 1_000;

export type GuestSessionWireState = {
  epoch: number;
  connectionEpoch: () => number;
  closed: () => boolean;
  hostCloseInProgress: () => boolean;
  isConnected: () => boolean;
  setConnected: (v: boolean) => void;
  hasDecodedFrame: () => boolean;
  setHasDecodedFrame: (v: boolean) => void;
  getPendingCloseReason: () => string | null;
  setPendingCloseReason: (v: string | null) => void;
  onHostCloseMessage: (reason: string) => void;
  onTransportLost: (source: string) => void;
  onIceDisconnected: (connection: IPeerConnection, isStale: () => boolean) => void;
  onIceFailed: (
    connection: IPeerConnection,
    isStale: () => boolean,
    iceEverChecking: boolean,
    alreadyRestarted: boolean,
  ) => void;
  cancelIceDisconnectGrace: () => void;
  startCongestionMonitoring: (connection: IPeerConnection) => void;
  setChannelOpenForCongestion: (open: boolean) => void;
  recordSdpBreadcrumb: (sdp: RTCSessionDescriptionInit) => void;
  waitForCandidatesThenForwardSdp: (
    connection: IPeerConnection,
    initialSdp: RTCSessionDescriptionInit,
    isStale: () => boolean,
  ) => void;
  scheduleDisconnect: (reason: string) => void;
  resetRetriesOnConnected: () => void;
  cancelRetry: () => void;
  isIceRestartInFlight: () => boolean;
  /**
   * Publishes a re-arm hook back to the session. Calling it clears the "already decoded,
   * stop asking" latch so exactly one further keyframe request may be sent — a mid-session
   * freeze otherwise had no way to ask for a fresh IDR.
   */
  publishKeyframeRearm?: (rearmAndRequest: () => boolean) => void;
  /**
   * Handle a latencyProbe datachannel message (the host's offset handshake). Returns true
   * when the message was consumed; false means it is not a latencyProbe message and the
   * caller should fall through to the app's data handler.
   */
  handleLatencyProbeMessage?: (data: string) => boolean;
  /**
   * Handle an inputPhoton frame tag (the host naming the frame that answers a sampled
   * input). Returns true when consumed; false falls through to the app's data handler.
   */
  handleInputPhotonMessage?: (data: string) => boolean;
  /**
   * Handle a standingLatencyDrain request (the host asking the receiver to re-assert or
   * pulse its latency hints). Returns true when consumed; false falls through to the app's
   * data handler.
   */
  handleDrainRequest?: (data: string) => boolean;
};

/**
 * Wires peer-lite events for a guest media session epoch.
 * Returns a dispose function that cancels the keyframe retry timer.
 */
export function wireGuestSessionEvents(
  connection: IPeerConnection,
  optimizationConfig: WebRtcOptimizationConfig,
  optimizationManager: WebRtcOptimizationManager,
  callbacks: GuestPeerSessionCallbacks,
  logger: GuestPeerLogger | undefined,
  state: GuestSessionWireState,
): () => void {
  const epoch = state.epoch;
  const isStale = () => epoch !== state.connectionEpoch();
  let iceEverChecking = false;
  let iceRestartAttempted = false;
  let iceConnected = false;
  let channelReady = false;
  let remoteVideoReady = false;
  let keyframeSentOnce = false;
  let keyframeRetryTimer: ReturnType<typeof setInterval> | null = null;
  const activityMonitor = createGuestPlayerActivityMonitor({
    send: (json) => connection.send(json),
  });
  let detachActivity: (() => void) | null = null;

  const applyReceiverLowLatency = () => {
    if (isStale()) return;
    // The shared config's 80 ms audio floor is the host's microphone receiver. The game
    // audio this guest plays uses the same hint API, and an 80 ms floor is the hold we
    // are trying to get off. Video stays at 0. separateAudioMsid keeps this off the picture.
    optimizationManager.applyPostConnectionOptimizations(
      connection.getRtcPeerConnection(),
      { ...optimizationConfig, audioJitterBufferTarget: 0 },
    );
  };

  const stopKeyframeRetries = () => {
    if (keyframeRetryTimer) {
      clearInterval(keyframeRetryTimer);
      keyframeRetryTimer = null;
    }
  };

  /**
   * Set while a re-arm is outstanding. It lets one request through after the first decoded
   * frame without restarting the startup retry loop, which exists only to get the stream
   * painting in the first place.
   */
  let keyframeRearmed = false;

  const sendKeyframeIfNeeded = (fromRetry: boolean) => {
    if (isStale() || (state.hasDecodedFrame() && !keyframeRearmed)) {
      stopKeyframeRetries();
      return;
    }
    if (!remoteVideoReady || !channelReady) return;
    // Track/channel events must not spam; retries use the interval until decoded. A re-armed
    // request is explicit and bypasses the once-only guard.
    if (!fromRetry && keyframeSentOnce && !keyframeRearmed) return;
    const sent = connection.send(JSON.stringify(REQUEST_KEYFRAME_MESSAGE));
    if (sent) {
      keyframeSentOnce = true;
      keyframeRearmed = false;
      logger?.info?.('Sent requestKeyframe');
    }
  };

  /**
   * Re-arm and attempt exactly one keyframe request. Returns whether it reached the wire.
   * Routed through sendKeyframeIfNeeded rather than sending directly so the readiness
   * guards (remote video present, data channel open) apply to this path too.
   */
  const rearmAndRequestKeyframe = (): boolean => {
    if (isStale()) return false;
    keyframeRearmed = true;
    const before = state.hasDecodedFrame();
    sendKeyframeIfNeeded(false);
    // keyframeRearmed is cleared by a successful send.
    const sent = !keyframeRearmed;
    if (!sent && before) {
      // Nothing went out — drop the arm so it cannot fire later unattended.
      keyframeRearmed = false;
    }
    return sent;
  };
  state.publishKeyframeRearm?.(rearmAndRequestKeyframe);

  const ensureKeyframeRetries = () => {
    if (isStale() || state.hasDecodedFrame()) {
      stopKeyframeRetries();
      return;
    }
    if (!remoteVideoReady || !channelReady) return;
    sendKeyframeIfNeeded(false);
    if (!keyframeRetryTimer && !state.hasDecodedFrame()) {
      keyframeRetryTimer = setInterval(() => {
        sendKeyframeIfNeeded(true);
      }, KEYFRAME_RETRY_MS);
    }
  };

  const maybeNotifyConnected = () => {
    if (isStale() || state.isConnected()) return;
    if (!iceConnected || !channelReady) return;
    if (!remoteVideoReady) {
      logger?.info?.('ICE/data channel ready before first remote video frame');
    }
    state.setConnected(true);
    state.resetRetriesOnConnected();
    state.cancelIceDisconnectGrace();
    applyReceiverLowLatency();
    state.startCongestionMonitoring(connection);
    callbacks.onConnected();
  };

  const handleTransportLost = (source: string) => {
    if (state.hostCloseInProgress() || isStale() || state.closed()) return;
    if (!state.isConnected()) return;
    // In-place restartIce owns this failure; a full rebuild here would tear down
    // the PC that restart is about to recover.
    if (state.isIceRestartInFlight()) return;
    logger?.warn?.(`Transport lost (${source})`);
    logger?.breadcrumb?.(`Transport lost: ${source}`, 'warning');
    state.setPendingCloseReason('ice_disconnected');
    state.onTransportLost('ice_disconnected');
  };

  connection.on('connected', () => {
    iceConnected = true;
    maybeNotifyConnected();
  });

  connection.on('channelOpen', () => {
    channelReady = true;
    state.setChannelOpenForCongestion(true);
    ensureKeyframeRetries();
    if (typeof globalThis !== 'undefined' && (globalThis as { window?: Window }).window) {
      detachActivity?.();
      detachActivity = activityMonitor.attachDomListeners(
        (globalThis as { window: Window & typeof globalThis }).window,
      );
    }
    maybeNotifyConnected();
  });

  connection.on('channelClosed', () => {
    channelReady = false;
    state.setChannelOpenForCongestion(false);
  });

  const rtcPc = connection.getRtcPeerConnection();
  if (rtcPc) {
    rtcPc.addEventListener('connectionstatechange', () => {
      if (isStale()) return;
      const connState = rtcPc.connectionState;
      if (connState === 'connected') {
        iceConnected = true;
        maybeNotifyConnected();
      } else if (connState === 'failed') {
        handleTransportLost('connectionState:failed');
      }
    });
  }

  connection.on('disconnected', () => {
    if (state.hostCloseInProgress() || isStale()) return;
    state.setConnected(false);
    state.scheduleDisconnect(state.getPendingCloseReason() ?? 'peer_disconnected');
  });

  connection.on('signal', (...args: unknown[]) => {
    if (isStale()) return;
    const sdp = args[0] as RTCSessionDescriptionInit;
    state.recordSdpBreadcrumb(sdp);
    if (sdp.type === 'answer') {
      state.waitForCandidatesThenForwardSdp(connection, sdp, isStale);
    } else {
      callbacks.onSignalGenerated(sdp);
    }
  });

  connection.on('onicecandidates', (...args: unknown[]) => {
    callbacks.onIceCandidatesGenerated(args[0] as RTCIceCandidate[]);
  });

  connection.on('streamRemote', (...args: unknown[]) => {
    if (isStale()) return;
    const stream = args[0] as MediaStream;
    if (stream.getVideoTracks().length > 0) {
      remoteVideoReady = true;
      applyReceiverLowLatency();
      ensureKeyframeRetries();
    }
    callbacks.onRemoteStream(stream);
    maybeNotifyConnected();
  });

  connection.on('channelData', (...args: unknown[]) => {
    const event = args[0] as { data: string | ArrayBuffer };
    if (typeof event.data === 'string' && tryHandleCloseMessage(event.data, state)) return;
    // The latency probe handshake is consumed by the session before the app's handler
    // sees it; the app has no use for the ping/pong traffic.
    if (
      typeof event.data === 'string' &&
      state.handleLatencyProbeMessage?.(event.data) === true
    ) {
      return;
    }
    // Same contract as the probe handshake: measurement traffic is session-internal and
    // the app's message handler has no use for it.
    if (
      typeof event.data === 'string' &&
      state.handleInputPhotonMessage?.(event.data) === true
    ) {
      return;
    }
    // Drain requests act on the receiver and answer on this channel; session-internal too.
    if (typeof event.data === 'string' && state.handleDrainRequest?.(event.data) === true) {
      return;
    }
    callbacks.onDataMessage(event.data);
  });

  connection.on('error', (...args: unknown[]) => {
    const error = args[0] as { id: string; message: string; error?: Error };
    logger?.error?.(`Peer error: ${error?.message}`, { error: String(error?.error ?? '') });
  });

  const rtc = connection.getRtcPeerConnection();
  if (rtc) {
    rtc.oniceconnectionstatechange = () => {
      if (isStale()) return;
      const iceState = rtc.iceConnectionState;
      if (iceState === 'checking') {
        iceEverChecking = true;
        callbacks.onIceChecking?.();
      } else if (iceState === 'connected' || iceState === 'completed') {
        iceRestartAttempted = false;
        state.cancelRetry();
        state.cancelIceDisconnectGrace();
        iceConnected = true;
        maybeNotifyConnected();
        callbacks.onIceConnected?.();
      } else if (iceState === 'disconnected') {
        state.onIceDisconnected(connection, isStale);
      } else if (iceState === 'failed') {
        state.cancelIceDisconnectGrace();
        state.setPendingCloseReason('ice_failed');
        callbacks.onIceFailed?.();
        state.onIceFailed(connection, isStale, iceEverChecking, iceRestartAttempted);
        iceRestartAttempted = true;
      }
    };
  }

  // Apply receiver LL as soon as recvonly transceivers exist (before first paint).
  applyReceiverLowLatency();

  return () => {
    stopKeyframeRetries();
    detachActivity?.();
    detachActivity = null;
    activityMonitor.dispose();
  };
}

function tryHandleCloseMessage(data: string, state: GuestSessionWireState): boolean {
  try {
    const parsed = JSON.parse(data);
    if (parsed?.__peerClose && typeof parsed.reason === 'string') {
      state.setPendingCloseReason(parsed.reason);
      state.onHostCloseMessage(parsed.reason);
      return true;
    }
  } catch {
    // Not JSON or not a close message
  }
  return false;
}

export function decideAndHandleIceFailure(
  connection: IPeerConnection,
  iceHealth: typeof IceHealthGuard,
  ctx: { iceEverChecking: boolean; alreadyRestarted: boolean },
  onRecovery: (reason: string) => void,
  logger?: GuestPeerLogger,
  onRestartAttempted?: () => void,
): void {
  const decision = iceHealth.decideIceFailureAction({
    iceEverChecking: ctx.iceEverChecking,
    alreadyRestarted: ctx.alreadyRestarted,
  });

  if (decision.action === 'instant_retry') {
    logger?.warn?.('Instant ICE failure (never reached checking)');
    logger?.breadcrumb?.('Instant ICE failure — no checking state', 'warning');
    onRecovery('ice_instant_fail');
  } else if (decision.action === 'restart') {
    logger?.info?.('ICE failed — attempting ICE restart');
    logger?.breadcrumb?.('Attempting ICE restart', 'info');
    onRestartAttempted?.();
    void connection.restartIce().catch((err) => {
      logger?.error?.('ICE restart failed', { error: String(err) });
      onRecovery('ice_restart_exhausted');
    });
  } else {
    logger?.warn?.('ICE restart exhausted — reconnecting with fresh PC');
    logger?.breadcrumb?.('ICE restart exhausted, full reconnect', 'warning');
    onRecovery('ice_restart_exhausted');
  }
}
